import { Download } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { safeUrl } from "@/lib/sanitize";
import { listSystemEvents } from "@/features/knowledge/api";
import type { KbSystemEvent } from "@/features/knowledge/types";

const GROUPS = [
  { type: "MT_MEDIA", title: "Social Media" },
  { type: "MT_EMAILS", title: "Emails" },
  { type: "MT_LETTERS", title: "Letters & Printables" },
  { type: "MT_BOOKS", title: "Handbooks" },
];

function ToolboxGroup({ type, title }: { type: string; title: string }) {
  const [items, setItems] = useState<KbSystemEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    listSystemEvents(type)
      .then((data) => !cancelled && setItems(data))
      .catch(() => !cancelled && toast.error("Could not load toolbox items."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [type]);

  return (
    <Card className="p-5">
      <h2 className="mb-3 font-semibold">{title}</h2>
      {loading ? (
        <Skeleton className="h-8 w-full" />
      ) : items.length === 0 ? (
        <p className="text-muted-foreground text-sm">Nothing here yet.</p>
      ) : (
        <ul className="divide-y">
          {items.map((item) => (
            <li key={item.id}>
              <a
                href={safeUrl(item.href)}
                target={item.newTab ? "_blank" : undefined}
                rel="noopener noreferrer"
                className="hover:bg-accent flex items-center gap-3 rounded-md px-2 py-2 text-sm"
              >
                <Download className="text-muted-foreground size-4 shrink-0" />
                <span className="flex-1">{item.title}</span>
                {item.badge && <Badge variant="secondary">{item.badge}</Badge>}
              </a>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export default function ToolboxPage() {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {GROUPS.map((g) => (
        <ToolboxGroup key={g.type} type={g.type} title={g.title} />
      ))}
    </div>
  );
}
